/* ============================================================
   La Nuit — cart-drawer.js
   Wires [data-cart-toggle] buttons to the global <modal-dialog id="CartDrawer">.
   Quantity changes and removals mutate the cart by stable line-item KEY
   (indices shift as items are removed), with a single concurrency guard
   and optimistic feedback so a tap is never lost or doubled.
   Listeners are delegated on the persistent body container, so they
   survive the innerHTML re-render after every mutation.
   ============================================================ */

(function () {
  const drawer = document.getElementById('CartDrawer');
  if (!drawer) return;

  const body = drawer.querySelector('[data-cart-body]');
  let releaseDrawerFocus = null;
  let busy = false;

  /* ---------- Lazy-load the delivery-detail capture script ----------
     checkout-capture.js (~26KB) is only needed when the cart is opened
     or a checkout is attempted. We inject it on first drawer open so it
     no longer parses on every page. Its src is passed on this script's
     own tag (data-checkout-capture-src). Loads at most once. */
  let captureLoaded = false;
  function loadCheckoutCapture() {
    if (captureLoaded) return;
    captureLoaded = true;
    const src = document.currentScript?.dataset.checkoutCaptureSrc
      || document.querySelector('script[data-checkout-capture-src]')?.dataset.checkoutCaptureSrc;
    if (!src) return;
    const s = document.createElement('script');
    s.src = src;
    s.defer = true;
    document.head.appendChild(s);
  }

  /* ---------- Open / close ---------- */

  function openDrawer(opener) {
    loadCheckoutCapture();
    drawer.removeAttribute('hidden');
    requestAnimationFrame(() => drawer.classList.add('is-open'));
    document.body.style.overflow = 'hidden';
    releaseDrawerFocus = window.LaNuit?.trapFocus(drawer, closeDrawer);
    if (opener) drawer._opener = opener;
  }

  function closeDrawer() {
    drawer.classList.remove('is-open');
    document.body.style.overflow = '';
    releaseDrawerFocus?.();
    releaseDrawerFocus = null;
    setTimeout(() => drawer.setAttribute('hidden', ''), 320);
    drawer._opener?.focus?.();
  }

  document.querySelectorAll('[data-cart-toggle]').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      openDrawer(btn);
    });
  });

  drawer.querySelectorAll('[data-modal-close]').forEach(el =>
    el.addEventListener('click', closeDrawer)
  );

  drawer.addEventListener('click', (e) => {
    if (e.target === drawer) closeDrawer();
  });

  /* ---------- Cart mutation by stable line-item key ---------- */

  function changeLine(idOrLine, quantity) {
    const payload = (typeof idOrLine === 'string' && idOrLine.length)
      ? { id: idOrLine, quantity }
      : { line: idOrLine, quantity };
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    }).then(r => {
      if (!r.ok) throw new Error('cart change failed');
      return r.json();
    });
  }

  async function refresh() {
    try {
      const res = await fetch(`${window.location.pathname}?section_id=cart-drawer`, { headers: { Accept: 'text/html' } });
      if (res.ok && body) {
        const html = await res.text();
        const fragment = new DOMParser().parseFromString(html, 'text/html');
        const newBody = fragment.querySelector('[data-cart-body]');
        if (newBody) body.innerHTML = newBody.innerHTML;
      }
    } catch (e) {
      console.error('Cart refresh failed', e);
    }
    try {
      const cart = await window.LaNuit?.Cart.get();
      if (cart) window.LaNuit?.publishCart(cart);
    } catch (e) { /* header count will catch up on next mutation */ }
  }

  async function handleRemove(btn) {
    if (busy) return;
    const row = btn.closest('[data-cart-line]');
    if (!row) return;
    const key = row.dataset.key;
    const line = parseInt(btn.dataset.line, 10);
    const title = row.querySelector('.cart-drawer__item-title')?.textContent.trim();

    busy = true;
    row.classList.add('is-removing'); // instant feedback; also blocks re-tap (pointer-events:none)

    try {
      await changeLine(key || line, 0);
      await refresh();
      if (window.LaNuit?.toast) {
        window.LaNuit.toast(title ? `Removed "${title}" from your cart` : 'Removed from your cart', { variant: 'info' });
      }
    } catch (err) {
      console.error('Cart remove failed', err);
      row.classList.remove('is-removing');
      if (window.LaNuit?.toast) {
        window.LaNuit.toast('Couldn’t remove that item — please try again.', { variant: 'error' });
      }
    } finally {
      busy = false;
    }
  }

  async function handleQty(input) {
    if (busy) return;
    const row = input.closest('[data-cart-line]');
    const key = row?.dataset.key;
    const line = parseInt(input.dataset.line, 10);
    const quantity = Math.max(0, parseInt(input.value, 10) || 0);

    busy = true;
    if (row) row.classList.add('is-busy');
    try {
      await changeLine(key || line, quantity);
      await refresh();
    } catch (err) {
      console.error('Cart change failed', err);
      await refresh(); // resync the displayed quantity to the server truth
    } finally {
      busy = false;
    }
  }

  async function handleUpsell(btn) {
    if (busy) return;
    const variantId = btn.dataset.variantId;
    if (!variantId) return;
    const card = btn.closest('[data-cart-upsell]');
    const error = card?.querySelector('[data-upsell-error]');
    busy = true;
    btn.disabled = true;
    if (card) card.classList.add('is-busy');
    if (error) error.hidden = true;
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
      });
      if (!res.ok) throw new Error('Cart recommendation add failed');
      await refresh();
    } catch (err) {
      console.error('Cart upsell add failed', err);
      await refresh();
      const currentCard = Array.from(body.querySelectorAll('[data-cart-upsell]'))
        .find(item => item.dataset.productId === card?.dataset.productId);
      const currentError = currentCard?.querySelector('[data-upsell-error]');
      if (currentError) {
        currentError.textContent = currentCard.dataset.addError;
        currentError.hidden = false;
      }
    } finally {
      busy = false;
      btn.disabled = false;
      if (card) card.classList.remove('is-busy');
    }
  }

  async function applyPromo(input, status) {
    if (!input) return;
    const code = (input.value || '').trim();
    if (!code) return;
    if (status) { status.hidden = false; status.textContent = 'Applying ' + code + '…'; status.classList.remove('is-error', 'is-success'); }
    try {
      const res = await fetch(`/discount/${encodeURIComponent(code)}?redirect=/cart`, { method: 'GET' });
      if (!res.ok) throw new Error('bad');
      if (status) {
        status.textContent = `"${code}" will be applied at checkout.`;
        status.classList.add('is-success');
      }
    } catch (e) {
      if (status) {
        status.textContent = `We couldn't apply "${code}". Try again at checkout.`;
        status.classList.add('is-error');
      }
    }
  }

  /* ---------- Delegated events on the persistent body container ----------
     The body element itself is never replaced (only its innerHTML), so a
     single set of delegated listeners survives every refresh — no rebinding,
     no lost handlers, no stale per-row listeners. */

  if (body) {
    body.addEventListener('click', (e) => {
      const removeBtn = e.target.closest('[data-cart-remove]');
      if (removeBtn) { e.preventDefault(); handleRemove(removeBtn); return; }

      const upsellBtn = e.target.closest('[data-upsell-add]');
      if (upsellBtn) { e.preventDefault(); handleUpsell(upsellBtn); return; }

      const applyBtn = e.target.closest('[data-promo-apply]');
      if (applyBtn) {
        const scope = applyBtn.closest('.cart-drawer__promo') || body;
        applyPromo(scope.querySelector('[data-promo-input]'), scope.querySelector('[data-promo-status]'));
      }
    });

    let qtyTimer = null;
    body.addEventListener('change', (e) => {
      const variantSelect = e.target.closest('[data-upsell-variant]');
      if (variantSelect) {
        const card = variantSelect.closest('[data-cart-upsell]');
        const option = variantSelect.selectedOptions[0];
        const add = card?.querySelector('[data-upsell-add]');
        const price = card?.querySelector('[data-upsell-price]');
        const image = card?.querySelector('.cart-upsell__image img');
        if (add && option) add.dataset.variantId = option.value;
        if (price && option) price.textContent = option.dataset.price;
        if (image && option?.dataset.image) {
          image.removeAttribute('srcset');
          image.src = option.dataset.image;
        }
        return;
      }
      const input = e.target.closest('[data-cart-line-input]');
      if (!input) return;
      clearTimeout(qtyTimer);
      qtyTimer = setTimeout(() => handleQty(input), 150);
    });

    body.addEventListener('keydown', (e) => {
      const promoInput = e.target.closest('[data-promo-input]');
      if (promoInput && e.key === 'Enter') {
        e.preventDefault();
        const scope = promoInput.closest('.cart-drawer__promo') || body;
        applyPromo(promoInput, scope.querySelector('[data-promo-status]'));
      }
    });
  }

  /* ---------- Open drawer + refresh when product is added elsewhere ---------- */

  /* ---------- External refresh requests (gift-wrap, free-gift, etc.) ---------- */

  document.addEventListener('cart:refresh', () => { refresh(); });

  document.addEventListener('cart:added', (e) => {
    refresh();
    openDrawer();
    if (window.LaNuit?.toast) {
      const title = e && e.detail && e.detail.title;
      const preorder = e && e.detail && e.detail.preorder;
      let msg;
      if (preorder) {
        msg = title
          ? `Pre-order placed for “${title}” — we’ll confirm by email`
          : 'Pre-order placed — we’ll confirm by email';
      } else {
        msg = title ? `Added “${title}” to your cart` : 'Added to your cart';
      }
      window.LaNuit.toast(msg, { variant: 'success' });
    }
  });
})();

